import React, { useState } from 'react';
import Chart from 'react-google-charts';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Heading, Loading, RadioButton, Text } from '../../Elements/Elements';
import { Progress, Quantity } from '../parts/parts';
import Legend from './Legend/Legend';

import { activeUsersPeriods, options } from './helpers/config';
import { emptyAreaChart } from '../helpers/config/helpers';
import { isObjNotEmpty } from 'utils/common';
import { pushData, typeToDateFormat } from './helpers/helper';

import classes from './AreaBig.module.scss';

const AreaChartBig = ({ chartData, loader }) => {
  const { t } = useTranslation(['activity']);

  const [activeUsersPeriod, setActiveUsersPeriod] = useState(
    activeUsersPeriods[2]
  );
  const [showTotal, setShowTotal] = useState(true);
  const [showUtil, setShowUtil] = useState(true);
  const [showActive, setShowActive] = useState(true);
  let maxChartData = 0;
  let activeUsersCurent = 0;
  let numOfTmLicenses = 0;
  let percentActiveUsers = 0;
  let avg = 0;
  const chartArr = [];
  let rateText = 'MAU Rate ';
  let avgText = 'MAU Avg ';

  function calcData(data, type) {
    const dataArr = [];
    const firstLine = [
      type,
      { role: 'tooltip', type: 'string', p: { html: true } }
    ];
    const dateFoormat = typeToDateFormat(type);
    options.pointSize = 4;
    options.tooltip.trigger = 'focus';
    options.colors = [];
    if (showTotal) {
      dataArr.push('numOfTmLicenses');
      firstLine.push(t('activity:customers.TOTAL_LICENSES'), {
        type: 'string',
        role: 'style'
      });
      options.colors.push('#79a0ed');
    }
    if (showUtil) {
      dataArr.push('numOfUsers');
      firstLine.push(t('activity:customers.TOTAL_USERS'), {
        type: 'string',
        role: 'style'
      });
      options.colors.push('#5189f7');
    }
    if (showActive) {
      dataArr.push('activeUsers');
      firstLine.push(t('activity:customers.ACTIVE_USERS'), {
        type: 'string',
        role: 'style'
      });
      options.colors.push('#2a5dca');
    }
    chartArr.push(firstLine);

    const count = dataArr.length;

    if (type === 'days') {
      if (data.length < 14) {
        const date = moment(data[0].date).subtract(1, type).format(dateFoormat);
        pushData(chartArr, date, count);
      }
    } else {
      if (data.length < 12) {
        const date = moment(data[0].date).subtract(1, type).format(dateFoormat);
        pushData(chartArr, date, count);
      }
    }

    data.map(item => {
      const date = moment(item.date).format(dateFoormat);

      if (count === 3) {
        maxChartData =
          maxChartData < item.numOfTmLicenses
            ? item.numOfTmLicenses
            : maxChartData;

        pushData(
          chartArr,
          date,
          count,
          options.colors,
          item.numOfTmLicenses,
          item.numOfUsers,
          item.activeUsers
        );
      } else if (count === 2) {
        maxChartData =
          maxChartData < item[dataArr[0]] ? item[dataArr[0]] : maxChartData;
        pushData(
          chartArr,
          date,
          count,
          options.colors,
          item[dataArr[0]],
          item[dataArr[1]]
        );
      } else {
        maxChartData =
          maxChartData < item[dataArr[0]] ? item[dataArr[0]] : maxChartData;
        pushData(chartArr, date, count, options.colors, item[dataArr[0]]);
      }

      return '';
    });
  }

  function usersStatistic(data) {
    activeUsersCurent =
      data[data.length - 1].activeUsers === null
        ? 0
        : data[data.length - 1].activeUsers;
    numOfTmLicenses =
      data[data.length - 1].numOfTmLicenses === null
        ? 0
        : data[data.length - 1].numOfTmLicenses;
    if (numOfTmLicenses) {
      percentActiveUsers = (data[data.length - 1].rate * 100).toFixed(0);
      avg = data[data.length - 1].avgDau;
    } else {
      percentActiveUsers = 0;
      avg = 0;
    }
  }

  const isEmpty = () => {
    return showTotal || showUtil || showActive;
  };

  if (isObjNotEmpty(chartData)) {
    switch (activeUsersPeriod) {
      case activeUsersPeriods[2]:
      default: {
        if (
          Object.prototype.hasOwnProperty.call(
            chartData,
            'activeUsersMonthly'
          ) &&
          chartData.activeUsersMonthly !== null &&
          chartData.activeUsersMonthly.length > 0
        ) {
          rateText = 'MAU Rate ';
          avgText = 'MAU Avg ';
          const data = chartData.activeUsersMonthly;
          isEmpty()
            ? calcData(data, 'months')
            : emptyAreaChart(chartArr, options);
          usersStatistic(data);
        } else {
          emptyAreaChart(chartArr, options);
        }
        break;
      }
      case activeUsersPeriods[3]: {
        if (
          Object.prototype.hasOwnProperty.call(
            chartData,
            'activeUsersYearly'
          ) &&
          chartData.activeUsersYearly !== null &&
          chartData.activeUsersYearly.length > 0
        ) {
          rateText = 'MAU Rate ';
          avgText = 'MAU Avg ';
          const data = chartData.activeUsersYearly;
          isEmpty()
            ? calcData(data, 'years')
            : emptyAreaChart(chartArr, options);
          usersStatistic(data);
        } else {
          emptyAreaChart(chartArr, options);
        }
        break;
      }
      case activeUsersPeriods[0]: {
        if (
          Object.prototype.hasOwnProperty.call(chartData, 'activeUsersDaily') &&
          chartData.activeUsersDaily !== null &&
          chartData.activeUsersDaily.length > 0
        ) {
          rateText = 'DAU Rate ';
          avgText = 'DAU Avg ';
          const data = chartData.activeUsersDaily;
          isEmpty()
            ? calcData(data, 'days')
            : emptyAreaChart(chartArr, options);
          usersStatistic(data);
        } else {
          emptyAreaChart(chartArr, options);
        }
        break;
      }
      case activeUsersPeriods[1]: {
        if (
          Object.prototype.hasOwnProperty.call(
            chartData,
            'activeUsersWeekly'
          ) &&
          chartData.activeUsersWeekly !== null &&
          chartData.activeUsersWeekly.length > 0
        ) {
          rateText = 'WAU Rate ';
          avgText = 'WAU Avg ';
          const data = chartData.activeUsersWeekly;
          isEmpty()
            ? calcData(data, 'weeks')
            : emptyAreaChart(chartArr, options);
          usersStatistic(data);
        } else {
          emptyAreaChart(chartArr, options);
        }
        break;
      }
    }
    if (maxChartData >= 10) {
      options.vAxis.maxValue = maxChartData * 1.2;
      if (options.vAxis.maxValue > 1000) options.chartArea.left = 32;
    }
  } else {
    emptyAreaChart(chartArr, options);
  }

  const handleActiveUsersPeriod = e => {
    if (e.target.value !== activeUsersPeriod) {
      setActiveUsersPeriod(e.target.value);
    }
  };

  const labelChange = e => {
    if (e.target.name === 'totalLicense') {
      setShowTotal(!showTotal);
    }
    if (e.target.name === 'utilizedLicense') {
      setShowUtil(!showUtil);
    }
    if (e.target.name === 'activeUsers') {
      setShowActive(!showActive);
    }
  };

  return (
    <section className={classes.box}>
      {!loader ? (
        <>
          <Heading>{t('activity:customers.ACTIVE_USERS')}</Heading>
          <div className={classes.info}>
            <Text type="p" className={classes.info_wrap}>
              <Quantity how={activeUsersCurent} from={numOfTmLicenses} />
              <Progress progress={percentActiveUsers} preText={rateText} />
              <Progress progress={avg} preText={avgText} percent={false} />
            </Text>
            <RadioButton
              handleUnit={handleActiveUsersPeriod}
              unit={activeUsersPeriod}
              units={activeUsersPeriods}
              name={t('activity:customers.ACTIVE_USERS_PERIOD')}
              type="c"
            />
          </div>
          <div className={classes.content}>
            <Chart chartType="AreaChart" options={options} data={chartArr} />
            <Legend
              labelChange={labelChange}
              showTotal={showTotal}
              showUtil={showUtil}
              showActive={showActive}
            />
          </div>
        </>
      ) : (
        <Loading />
      )}
    </section>
  );
};

AreaChartBig.propTypes = {
  chartData: PropTypes.object,
  loader: PropTypes.bool
};

export default AreaChartBig;
